const normalizeToken = (value) => String(value ?? '').trim().toLowerCase();

const toSearchableValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => toSearchableValue(item))
      .filter(Boolean)
      .join(' ');
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return '';
    }
  }
  return '';
};

const buildSubsequenceScore = (token, text) => {
  let tokenIdx = 0;
  let textIdx = 0;
  let totalGap = 0;
  let previousMatchIdx = -1;

  while (tokenIdx < token.length && textIdx < text.length) {
    if (token[tokenIdx] === text[textIdx]) {
      if (previousMatchIdx >= 0) {
        totalGap += Math.max(0, textIdx - previousMatchIdx - 1);
      }
      previousMatchIdx = textIdx;
      tokenIdx += 1;
    }
    textIdx += 1;
  }

  if (tokenIdx !== token.length) return 0;
  const compactnessBonus = Math.max(0, 20 - totalGap);
  return 35 + compactnessBonus;
};

const scoreToken = (token, text) => {
  if (!token || !text) return 0;
  const isNumericToken = /^\d+$/.test(token);
  if (isNumericToken && token.length >= 4) {
    return text.includes(token) ? 140 : 0;
  }
  const exactIdx = text.indexOf(token);
  if (exactIdx >= 0) {
    const proximityBonus = Math.max(0, 24 - exactIdx);
    return 80 + proximityBonus;
  }
  return buildSubsequenceScore(token, text);
};

export const buildRowSearchText = (row, fieldNames) => {
  const values = (fieldNames || []).map((field) => toSearchableValue(row?.[field]));
  return normalizeToken(values.join(' '));
};

export const fuzzyFilterRows = (rows, query, fieldNames) => {
  const normalizedQuery = normalizeToken(query);
  if (!normalizedQuery) return Array.isArray(rows) ? rows : [];

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  if (!tokens.length) return Array.isArray(rows) ? rows : [];

  return (Array.isArray(rows) ? rows : [])
    .map((row, index) => {
      const text = buildRowSearchText(row, fieldNames);
      if (!text) return null;

      let totalScore = 0;
      for (const token of tokens) {
        const tokenScore = scoreToken(token, text);
        if (tokenScore <= 0) return null;
        totalScore += tokenScore;
      }

      return { row, totalScore, index };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
};

export const multiSelectFilterFn = (row, id, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  const value = row.getValue(id);
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '')).some((item) => filterValue.includes(item));
  }
  if (typeof value === 'string' && value.includes(',')) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .some((item) => filterValue.includes(item));
  }
  return filterValue.includes(String(value ?? ''));
};
